import { useId, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/Modal';
import { AppRoutes } from '@/app/routes';
import { isLiveInputPath } from '@/app/routes/liveScreens';
import { useAuthController } from '@/features/auth/controller/auth.controller';
import { api } from '@/services/api';
import { HttpError } from '@/services/http';
import type { TermsStatus } from '@/types';
import { LEGAL_VERSIONS } from '../data/legalVersions';
import { consentLanguage, shownSentence } from '../data/shownConsent';

export const termsStatusKey = (userId: string) => ['terms-status', userId] as const;

/** Same marker as `useImpersonationExit`: an admin browsing as someone else accepts nothing for him. */
function isImpersonating(): boolean {
  try {
    return !!localStorage.getItem('originalAdminToken');
  } catch {
    return false;
  }
}

/** Legal pages stay readable from the boxes' links; the live input is never interrupted mid-match. */
function isExemptPath(pathname: string): boolean {
  return isLiveInputPath(pathname) || Object.values(AppRoutes.legal).some((path) => pathname.startsWith(path));
}

/**
 * Decision of 01/11/2026 (VOL-199): an account without any acceptance of the CGU
 * (or of the CGV, for a running subscription), or that accepted an older version
 * than `legal-versions.ts`, accepts the current one before going on. Asked once
 * per app start: a failed check (offline, older server) lets the user through.
 * Recorded like at payment, with the language and the sentences shown.
 */
export function TermsUpdateGate() {
  const userId = useAuthController((s) => s.user?.id ?? null);
  const { pathname } = useLocation();

  const { data } = useQuery({
    queryKey: termsStatusKey(userId ?? ''),
    queryFn: () => api.getTermsStatus(),
    enabled: userId !== null && !isImpersonating(),
    staleTime: Infinity,
    retry: false,
  });

  // A reply of another shape (server without the route behind a catch-all, proxy page) counts as a
  // failed check: this gate wraps every screen, the live input included, so it must never throw.
  const pending = data && Array.isArray(data.pending) ? data.pending : [];
  // During a release, server and app may disagree on the versions: wait for both to match.
  const sameVersions = data?.versions?.cgu === LEGAL_VERSIONS.cgu && data?.versions?.cgv === LEGAL_VERSIONS.cgv;
  if (!userId || pending.length === 0 || !sameVersions || isExemptPath(pathname)) return null;
  // Keyed by account: on a shared club tablet, boxes ticked by one coach are never the next one's consent.
  return <TermsUpdateDialog key={userId} userId={userId} pending={pending} />;
}

function TermsUpdateDialog({ userId, pending }: { userId: string; pending: TermsStatus['pending'] }) {
  const { t, i18n } = useTranslation('common');
  const logout = useAuthController((s) => s.logout);
  const queryClient = useQueryClient();
  const [cguAccepted, setCguAccepted] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const cguId = useId();
  const cgvId = useId();
  const hintId = useId();

  const accept = useMutation({
    mutationFn: () =>
      api.acceptTerms({
        language: consentLanguage(i18n),
        ...(pending.includes('CGU')
          ? { cguVersion: LEGAL_VERSIONS.cgu, cguText: shownSentence(t('terms_gate.accept_cgu')) }
          : {}),
        ...(pending.includes('CGV')
          ? { cgvVersion: LEGAL_VERSIONS.cgv, cgvText: shownSentence(t('terms_gate.accept_cgv')) }
          : {}),
      }),
    onSuccess: (status) => {
      queryClient.setQueryData(termsStatusKey(userId), status);
    },
    onError: (err) => {
      // 400: the server has newer versions than this app; reloading fetches them.
      toast.error(t(err instanceof HttpError && err.status === 400 ? 'terms_gate.outdated' : 'terms_gate.error'));
    },
  });

  const needsCgu = pending.includes('CGU');
  const needsCgv = pending.includes('CGV');
  const canAccept = (!needsCgu || cguAccepted) && (!needsCgv || cgvAccepted) && !accept.isPending;
  const checkbox =
    'mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 text-action focus-visible:ring-2 focus-visible:ring-action';
  const row = 'flex min-h-11 items-start gap-3 py-1';
  const link = 'text-action underline underline-offset-2 hover:text-action-hover';

  return (
    <Modal isOpen onClose={() => {}} title={t('terms_gate.title')} dismissible={false}>
      <p className="mb-4 text-sm text-slate-700">{t('terms_gate.body')}</p>
      <div className="space-y-2">
        {needsCgu && (
          <div className={row}>
            <input
              id={cguId}
              type="checkbox"
              checked={cguAccepted}
              onChange={(e) => setCguAccepted(e.target.checked)}
              className={checkbox}
            />
            <label htmlFor={cguId} className="cursor-pointer select-none text-sm leading-relaxed text-slate-700">
              <Trans
                t={t}
                i18nKey="terms_gate.accept_cgu"
                components={{ cgu: <Link to={AppRoutes.legal.cgu} target="_blank" rel="noopener noreferrer" className={link} /> }}
              />
            </label>
          </div>
        )}
        {needsCgv && (
          <div className={row}>
            <input
              id={cgvId}
              type="checkbox"
              checked={cgvAccepted}
              onChange={(e) => setCgvAccepted(e.target.checked)}
              className={checkbox}
            />
            <label htmlFor={cgvId} className="cursor-pointer select-none text-sm leading-relaxed text-slate-700">
              <Trans
                t={t}
                i18nKey="terms_gate.accept_cgv"
                components={{ cgv: <Link to={AppRoutes.legal.cgv} target="_blank" rel="noopener noreferrer" className={link} /> }}
              />
            </label>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:items-start sm:justify-end">
        <button
          type="button"
          onClick={() => void logout()}
          disabled={accept.isPending}
          className="min-h-11 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action disabled:opacity-60"
        >
          {t('terms_gate.logout')}
        </button>
        <div className="flex flex-col">
          <button
            type="button"
            onClick={() => accept.mutate()}
            disabled={!canAccept}
            aria-busy={accept.isPending}
            aria-describedby={canAccept || accept.isPending ? undefined : hintId}
            className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-action px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-action-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action disabled:pointer-events-none disabled:opacity-50"
          >
            {accept.isPending && <Loader2 size={15} className="animate-spin motion-reduce:animate-none" aria-hidden="true" />}
            {t('terms_gate.accept')}
          </button>
          {!canAccept && !accept.isPending && (
            <p id={hintId} className="mt-2 text-xs text-slate-500 sm:text-right">
              {t(needsCgu && needsCgv ? 'terms_gate.hint_both' : 'terms_gate.hint_one')}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
